import React, { createContext, useContext, useState, useCallback } from 'react';

const PortalRoleContext = createContext();

/**
 * Role priority order (highest to lowest).
 *
 * Exported because holding several roles at once is normal here — a manager who also teaches,
 * a teacher whose own child attends — and any screen that gathers something from every role a
 * person holds has to present them in the same order this does. Deriving that order separately
 * is how two screens end up disagreeing about who someone is.
 */
export const ROLE_PRIORITY = ['developer', 'system_admin', 'director', 'manager', 'admin', 'teacher', 'parent'];

export const usePortalRole = () => {
  const context = useContext(PortalRoleContext);
  if (!context) {
    throw new Error('usePortalRole must be used within PortalRoleProvider');
  }
  return context;
};

export const PortalRoleProvider = ({ children }) => {
  // Multi-role support: Store all user roles
  const [userRoles, setUserRoles] = useState(['director']);
  const [currentUserId, setCurrentUserId] = useState(5);
  const [currentUserCampus, setCurrentUserCampus] = useState(null);
  const [currentUserCampusIds, setCurrentUserCampusIds] = useState([]);

  // Get highest priority role (for backward compatibility)
  const getPrimaryRole = useCallback((roles) => {
    return ROLE_PRIORITY.find(r => roles.includes(r)) || null;
  }, []);

  // Current role is the highest priority role (backward compatible)
  const currentRole = getPrimaryRole(userRoles);

  // Helper function to check if user has a specific role
  const hasRole = useCallback((role) => {
    return userRoles.includes(role);
  }, [userRoles]);

  // Set all roles at once (for real auth integration)
  const setRoles = useCallback((roles, userId = null, campus = null, campusIds = []) => {
    setUserRoles(roles);

    if (userId) {
      setCurrentUserId(userId);
    }

    // Set campus if user has director, manager or admin role (campus-restricted)
    // Developer and system_admin are unrestricted (no campus lock)
    if (roles.includes('director') || roles.includes('manager') || roles.includes('admin')) {
      setCurrentUserCampusIds(campusIds || []);
      setCurrentUserCampus(campus || (campusIds?.length ? campusIds[0] : null));
    } else {
      setCurrentUserCampus(null);
      setCurrentUserCampusIds([]);
    }
  }, []);

  // Legacy switchRole for backward compatibility (switches to single role)
  const switchRole = useCallback((role, userId = null, campus = null, campusIds = []) => {
    setUserRoles([role]);

    if (userId) {
      setCurrentUserId(userId);
    } else {
      // Set default userId based on role
      if (role === 'system_admin') setCurrentUserId(6);
      else if (role === 'director') setCurrentUserId(5);
      else if (role === 'manager') setCurrentUserId(7);
      else if (role === 'admin') setCurrentUserId(8);
      else if (role === 'teacher') setCurrentUserId(1);
      else if (role === 'parent') setCurrentUserId(2);
    }

    if (role === 'director' || role === 'manager' || role === 'admin') {
      setCurrentUserCampusIds(campusIds || []);
      setCurrentUserCampus(campus || (campusIds?.length ? campusIds[0] : null));
    } else {
      setCurrentUserCampus(null);
      setCurrentUserCampusIds([]);
    }
  }, []);

  // Allow updating campus separately (e.g. when profile loads after role init)
  const updateCampus = useCallback((campus) => {
    setCurrentUserCampus(campus || null);
  }, []);

  // Allow updating all campus IDs (e.g. when profile loads)
  const updateCampusIds = useCallback((campusIds) => {
    setCurrentUserCampusIds(campusIds || []);
  }, []);

  return (
    <PortalRoleContext.Provider
      value={{
        // Multi-role support
        userRoles,
        hasRole,
        setRoles,
        // Backward compatible
        currentRole,       // Highest priority role
        currentUserId,
        currentUserCampus,
        currentUserCampusIds,
        updateCampus,
        updateCampusIds,
        switchRole         // Legacy: switches to single role
      }}
    >
      {children}
    </PortalRoleContext.Provider>
  );
};
